'use client'

import { cn } from '@brew/ui/cn'
import { Tabs, TabsList, TabsTrigger } from '@brew/ui/tabs'
import {
  ArrowUpRight,
  BookOpen,
  CircleCheck,
  CircleDashed,
  ExternalLink,
  Globe,
  Play,
  PlayCircle,
  Tag,
} from 'lucide-react'
import Image from 'next/image'
import { type ComponentType, type ReactNode, useEffect, useState } from 'react'
import type {
  IntegrationCapabilityMeta,
  IntegrationCapabilityStatus,
  IntegrationMeta,
} from '@/lib/integrations/catalog'
import { ICON } from '@/lib/ui/icon'
import { TYPE } from '@/lib/ui/typography'
import { IntegrationLogoTile } from '../components/integration-logo-tile'
import {
  DIRECTION_LABEL,
  PROVIDER_CATEGORY,
  TYPE_LABEL,
} from './integration-sidebar'
/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

export function Hero({
  integration,
  cta,
  status,
}: {
  integration: IntegrationMeta
  cta: ReactNode
  status: ReactNode
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
      <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
        <IntegrationLogoTile integration={integration} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <h1 className={TYPE.heading}>{integration.name}</h1>
            {status}
          </div>
          <p className={cn(TYPE.bodyMuted, 'mt-1.5 leading-snug')}>
            {integration.shortDescription}
          </p>
        </div>
      </div>
      {cta ? <div className="flex shrink-0 items-center">{cta}</div> : null}
    </header>
  )
}

/* -------------------------------------------------------------------------- */
/*  Hero thumbnail (small, inline, links out)                                 */
/* -------------------------------------------------------------------------- */

function ThumbnailIconBadge({
  icon: Icon,
}: {
  icon: ComponentType<{ className?: string }>
}) {
  return (
    <span
      className="absolute right-1.5 bottom-1.5 flex size-6 items-center justify-center rounded-lg bg-white/95 shadow-sm ring-1 ring-black/10"
      aria-hidden
    >
      <Icon className={cn(ICON.sm, 'text-foreground')} />
    </span>
  )
}

function HeroThumbnailFrame({
  href,
  ariaLabel,
  children,
}: {
  href: string
  ariaLabel: string
  children: ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="group relative block aspect-video w-full overflow-hidden rounded-lg border border-border bg-muted/30"
    >
      {children}
      <span
        className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15"
        aria-hidden
      />
    </a>
  )
}

function DocsHeroThumbnail({
  docsUrl,
  name,
}: {
  docsUrl: string
  name: string
}) {
  const [ogImage, setOgImage] = useState<string | null>(null)
  useEffect(() => {
    let isCancelled = false
    fetch(`/api/integrations/docs-og?url=${encodeURIComponent(docsUrl)}`)
      .then((res) =>
        res.ok ? (res.json() as Promise<{ ogImage: string | null }>) : null
      )
      .then((data) => {
        if (!isCancelled && data?.ogImage) {
          setOgImage(data.ogImage)
        }
      })
      .catch(() => {})
    return () => {
      isCancelled = true
    }
  }, [docsUrl])

  return (
    <HeroThumbnailFrame href={docsUrl} ariaLabel={`Open ${name} documentation`}>
      {ogImage ? (
        // biome-ignore lint/performance/noImgElement: arbitrary docs OG image host
        // biome-ignore lint/correctness/useImageSize: pre-Ultracite baseline — add dimensions when touching
        <img
          src={ogImage}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div
          className="flex h-full w-full flex-col gap-1.5 bg-muted/40 p-3"
          aria-hidden
        >
          <div className="h-1.5 w-1/2 rounded-full bg-foreground/15" />
          <div className="h-1 w-full rounded-full bg-foreground/10" />
          <div className="h-1 w-5/6 rounded-full bg-foreground/10" />
          <div className="h-1 w-3/4 rounded-full bg-foreground/10" />
        </div>
      )}
      <ThumbnailIconBadge icon={ExternalLink} />
    </HeroThumbnailFrame>
  )
}

function HeroThumbnail({ integration }: { integration: IntegrationMeta }) {
  if (integration.tutorialVideoUrl) {
    return (
      <HeroThumbnailFrame
        href={integration.tutorialVideoUrl}
        ariaLabel={`Watch ${integration.name} tutorial`}
      >
        {integration.tutorialVideoThumbnail ? (
          <Image
            src={integration.tutorialVideoThumbnail}
            alt=""
            width={320}
            height={180}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-muted/40" aria-hidden />
        )}
        <ThumbnailIconBadge icon={Play} />
      </HeroThumbnailFrame>
    )
  }
  if (integration.tutorialDocsUrl) {
    return (
      <DocsHeroThumbnail
        docsUrl={integration.tutorialDocsUrl}
        name={integration.name}
      />
    )
  }
  return null
}

/* -------------------------------------------------------------------------- */
/*  Two-column body shell                                                     */
/* -------------------------------------------------------------------------- */

export function BodyGrid({
  rail,
  content,
}: {
  rail: ReactNode
  content: ReactNode
}) {
  return (
    <div className="mt-8 grid gap-10 border-border border-t pt-8 lg:grid-cols-[200px_1fr] lg:gap-14">
      <aside className="flex flex-col gap-7 text-sm">{rail}</aside>
      <div className="flex min-w-0 flex-col gap-8">{content}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Left rail — meta cells                                                    */
/* -------------------------------------------------------------------------- */

function RailLabel({ children }: { children: ReactNode }) {
  return <p className={TYPE.supportingMedium}>{children}</p>
}

function RailRow({
  icon: Icon,
  children,
}: {
  icon?: ComponentType<{ className?: string }>
  children: ReactNode
}) {
  return (
    <div className={cn(TYPE.body, 'flex items-center gap-2')}>
      {Icon ? (
        <Icon
          className={cn(ICON.default, 'shrink-0 text-muted-foreground')}
          aria-hidden
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </div>
  )
}

const CAPABILITY_ICON: Record<
  IntegrationCapabilityStatus,
  ComponentType<{ className?: string }>
> = {
  live: CircleCheck,
  planned: CircleDashed,
  unsupported: CircleDashed,
}

const CAPABILITY_ICON_CLASS: Record<IntegrationCapabilityStatus, string> = {
  live: 'text-emerald-600 dark:text-emerald-400',
  planned: 'text-amber-600 dark:text-amber-400',
  unsupported: 'text-muted-foreground/60',
}

function RailCapabilities({
  capabilities,
}: {
  capabilities: ReadonlyArray<IntegrationCapabilityMeta>
}) {
  if (capabilities.length === 0) {
    return null
  }
  return (
    <div className="flex flex-col gap-2">
      <RailLabel>Capabilities</RailLabel>
      <ul className="flex flex-col gap-2">
        {capabilities.map((cap) => {
          const Icon = CAPABILITY_ICON[cap.status]
          const isLive = cap.status === 'live'
          return (
            <li key={cap.id} className="flex items-start gap-2">
              <Icon
                aria-hidden
                className={cn(
                  ICON.default,
                  'mt-0.5 shrink-0',
                  CAPABILITY_ICON_CLASS[cap.status]
                )}
              />
              <span
                className={cn(
                  'leading-snug',
                  isLive ? TYPE.body : TYPE.bodyMuted
                )}
              >
                {cap.label}
                {!isLive ? (
                  <span className={cn(TYPE.supporting, 'ml-1.5')}>
                    · {cap.status === 'planned' ? 'Planned' : 'Not supported'}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function RailResources({ integration }: { integration: IntegrationMeta }) {
  const links: Array<{
    label: string
    href: string
    icon: ComponentType<{ className?: string }>
  }> = []
  if (integration.brandDomain) {
    links.push({
      label: 'Website',
      href: `https://${integration.brandDomain}`,
      icon: Globe,
    })
  }
  if (integration.tutorialDocsUrl) {
    links.push({
      label: 'Documentation',
      href: integration.tutorialDocsUrl,
      icon: BookOpen,
    })
  }
  if (integration.tutorialVideoUrl) {
    links.push({
      label: 'Video tutorial',
      href: integration.tutorialVideoUrl,
      icon: PlayCircle,
    })
  }

  if (links.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      <RailLabel>Resources</RailLabel>
      <ul className="flex flex-col gap-1.5">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                TYPE.body,
                'group flex items-center gap-2 transition-colors hover:text-foreground'
              )}
            >
              <link.icon
                className={cn(ICON.md, 'shrink-0 text-muted-foreground')}
                aria-hidden
              />
              <span className="flex-1 truncate">{link.label}</span>
              <ArrowUpRight
                className={cn(
                  ICON.md,
                  'text-muted-foreground transition-colors group-hover:text-foreground'
                )}
                aria-hidden
              />
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function MetaRail({
  integration,
  capabilities,
}: {
  integration: IntegrationMeta
  capabilities: ReadonlyArray<IntegrationCapabilityMeta>
}) {
  const category = PROVIDER_CATEGORY[integration.id] ?? 'Integration'
  const direction = DIRECTION_LABEL[integration.dataDirection]

  let thumbnailLabel: string | null = null
  if (integration.tutorialVideoUrl) {
    thumbnailLabel = 'Tutorial'
  } else if (integration.tutorialDocsUrl) {
    thumbnailLabel = 'Documentation'
  }

  return (
    <>
      {thumbnailLabel ? (
        <div className="flex flex-col gap-2">
          <RailLabel>{thumbnailLabel}</RailLabel>
          <HeroThumbnail integration={integration} />
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        <RailLabel>Category</RailLabel>
        <RailRow icon={Tag}>{category}</RailRow>
      </div>
      <div className="flex flex-col gap-2">
        <RailLabel>Type</RailLabel>
        <RailRow>
          {TYPE_LABEL[integration.integrationType]} · {direction}
        </RailRow>
      </div>
      <div className="flex flex-col gap-2">
        <RailLabel>Pricing</RailLabel>
        <RailRow>Free</RailRow>
      </div>
      <div className="flex flex-col gap-2">
        <RailLabel>Author</RailLabel>
        <RailRow>{integration.author}</RailRow>
      </div>
      <RailCapabilities capabilities={capabilities} />
      <RailResources integration={integration} />
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*  Tab strip + section primitives                                            */
/* -------------------------------------------------------------------------- */

export interface TabDef {
  value: string
  label: string
  icon: ComponentType<{ className?: string }>
}

export function TabBar({
  value,
  onValueChange,
  tabs,
  children,
}: {
  value: string
  onValueChange: (next: string) => void
  tabs: ReadonlyArray<TabDef>
  children: ReactNode
}) {
  return (
    <Tabs value={value} onValueChange={onValueChange} className="w-full">
      <TabsList className="h-auto w-full justify-start rounded-none border-border border-b bg-transparent p-0 text-muted-foreground">
        {tabs.map((t) => (
          <TabsTrigger
            key={t.value}
            value={t.value}
            className={cn(
              'data-[state=active]:border-foreground data-[state=active]:text-foreground',
              'relative gap-1.5 rounded-none border-transparent border-b-2 bg-transparent px-3 pt-2 pb-2.5 shadow-none',
              TYPE.bodyMedium,
              'data-[state=active]:bg-transparent data-[state=active]:shadow-none',
              'transition-colors hover:text-foreground'
            )}
          >
            <t.icon className={cn(ICON.md, 'opacity-70')} aria-hidden />
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <div className="mt-6">{children}</div>
    </Tabs>
  )
}

export function ProseSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className={TYPE.heading}>{title}</h2>
      <div className={cn(TYPE.body, 'leading-relaxed')}>{children}</div>
    </section>
  )
}
